import { useEffect, useMemo, useState } from "react";

import {
  ActionButton,
  EmptyGridState,
  SectionCard,
} from "@/app/admin/system-catalog/systemCatalogAdminUi";
import { useToast } from "@/components/ui/ToastProvider";
import type { AdminTilesWorkspaceData } from "@/app/admin/adminTypes";
import {
  useAdminTilesWorkspaceQuery,
  useUploadAdminTilesMutation,
} from "@/lib/queries/adminQueries";

const emptyWorkspace: AdminTilesWorkspaceData = {
  tileSets: [],
  tiles: [],
  collections: [],
  taxonomyNodes: [],
};

export function AdminTilesWorkspace() {
  const toast = useToast();
  const {
    data: workspace = emptyWorkspace,
    error: workspaceError,
  } = useAdminTilesWorkspaceQuery();
  const uploadTilesMutation = useUploadAdminTilesMutation();
  const [targetMode, setTargetMode] = useState<"existing" | "new">("existing");
  const [selectedTileSetId, setSelectedTileSetId] = useState(workspace.tileSets[0]?.id ?? "");
  const [newTileSetTitle, setNewTileSetTitle] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!workspaceError) {
      return;
    }

    toast.error(workspaceError instanceof Error ? workspaceError.message : "Failed to refresh tiles workspace.");
  }, [toast, workspaceError]);

  const filteredTiles = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) {
      return workspace.tiles;
    }
    return workspace.tiles.filter((tile) =>
      `${tile.filename} ${tile.tileSetLabel ?? ""}`.toLowerCase().includes(query),
    );
  }, [filter, workspace.tiles]);

  async function handleUpload() {
    if (selectedFiles.length === 0) {
      toast.error("Choose one or more tile files to upload.");
      return;
    }
    const title =
      targetMode === "existing"
        ? workspace.tileSets.find((tileSet) => tileSet.id === selectedTileSetId)?.title ?? ""
        : newTileSetTitle;
    if (!title.trim()) {
      toast.error(targetMode === "existing" ? "Choose a tile set." : "New tile set title is required.");
      return;
    }
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.set(
        "payload",
        JSON.stringify({
          tileSetId: targetMode === "existing" ? selectedTileSetId : undefined,
          title,
        }),
      );
      for (const file of selectedFiles) {
        formData.append("files", file);
      }
      const payload = await uploadTilesMutation.mutateAsync(formData);
      setSelectedFiles([]);
      toast.success(`Uploaded ${payload.tiles?.length ?? selectedFiles.length} tile(s).`);
    } catch (caughtError) {
      toast.error(caughtError instanceof Error ? caughtError.message : "Failed to upload tiles.");
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[420px_minmax(0,1fr)]">
      <div className="space-y-6">
        <SectionCard
          title="Upload base tiles"
          description="Add one or more tile images into an existing tile set or create a new one."
        >
          <div className="space-y-4">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setTargetMode("existing")}
                className={[
                  "rounded-full border px-4 py-2 text-sm transition",
                  targetMode === "existing"
                    ? "border-[#3e664d]/50 bg-[#3e664d]/10 text-neutral-950"
                    : "border-neutral-950/15 bg-neutral-950/[0.035] text-neutral-700 hover:bg-[#fff2d9]",
                ].join(" ")}
              >
                Existing tile set
              </button>
              <button
                type="button"
                onClick={() => setTargetMode("new")}
                className={[
                  "rounded-full border px-4 py-2 text-sm transition",
                  targetMode === "new"
                    ? "border-[#3e664d]/50 bg-[#3e664d]/10 text-neutral-950"
                    : "border-neutral-950/15 bg-neutral-950/[0.035] text-neutral-700 hover:bg-[#fff2d9]",
                ].join(" ")}
              >
                New tile set
              </button>
            </div>

            {targetMode === "existing" ? (
              <label className="block">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-600">
                  Tile set
                </span>
                <select
                  value={selectedTileSetId}
                  onChange={(event) => setSelectedTileSetId(event.target.value)}
                  className="w-full rounded-xl border border-neutral-950/20 bg-white/60 px-4 py-3 text-sm text-neutral-950 outline-none transition focus:border-neutral-950/40"
                >
                  <option value="">Choose a tile set</option>
                  {workspace.tileSets.map((tileSet) => (
                    <option key={tileSet.id} value={tileSet.id}>
                      {tileSet.title}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label className="block">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-600">
                  New tile set title
                </span>
                <input
                  value={newTileSetTitle}
                  onChange={(event) => setNewTileSetTitle(event.target.value)}
                  className="w-full rounded-xl border border-neutral-950/20 bg-white/60 px-4 py-3 text-sm text-neutral-950 outline-none transition focus:border-neutral-950/40"
                />
              </label>
            )}

            <input
              type="file"
              accept="image/png,image/webp,image/jpeg"
              multiple
              onChange={(event) => setSelectedFiles(Array.from(event.target.files ?? []))}
              className="block w-full text-sm text-neutral-700"
            />

            <ActionButton onClick={handleUpload} disabled={isUploading}>
              {isUploading ? "Uploading..." : "Upload tiles"}
            </ActionButton>
          </div>
        </SectionCard>

        <SectionCard
          title="Tile sets"
          description="Existing enabled tile sets available to compose."
        >
          <div className="space-y-2">
            {workspace.tileSets.map((tileSet) => (
              <div
                key={tileSet.id}
                className="rounded-xl border border-neutral-950/15 bg-neutral-950/[0.035] px-3 py-2 text-sm text-neutral-800"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="truncate">{tileSet.title}</span>
                  <span className="text-xs text-neutral-600">{tileSet.enabled ? "Enabled" : "Disabled"}</span>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <SectionCard
        title="Base tile library"
        description="Review the tiles currently available as compose bases."
      >
        <div className="space-y-4">
          <input
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Filter tiles"
            className="w-full rounded-xl border border-neutral-950/20 bg-white/60 px-4 py-3 text-sm text-neutral-950 outline-none transition focus:border-neutral-950/40"
          />
          {filteredTiles.length > 0 ? (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-3">
              {filteredTiles.map((tile) => (
                <div
                  key={tile.id}
                  className="overflow-hidden rounded-2xl border-[3px] border-neutral-950 bg-[#fffaf1] shadow-[8px_8px_0_rgba(19,19,19,0.12)]"
                >
                  <img
                    src={tile.url}
                    alt={tile.filename}
                    className="aspect-square w-full bg-neutral-950/[0.06] object-cover"
                  />
                  <div className="space-y-1 px-3 py-3">
                    <p className="truncate text-sm font-medium text-neutral-950">{tile.filename}</p>
                    <p className="truncate text-xs text-neutral-600">{tile.tileSetLabel ?? "Unassigned"}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyGridState message="No base tiles match the current filter." />
          )}
        </div>
      </SectionCard>
    </div>
  );
}
